<template>
  <div class="mco">
    <header class="mco-hd">
      <div class="mco-logo">W</div>
      <h1 class="mco-title">WordCloudStudio</h1>
    </header>
    <p class="mco-loading">{{ $t('RedirectingToPayment') }}</p>
  </div>
</template>

<script>
import {
  getPassthroughId, getSelectedPlan,
  buildDeepLink, openDeepLink,
  stashPendingTransaction,
} from '@/utils/mobilePayment'

const PRICE_MAP = {
  annual: 'pri_01hv37ntpgqgf0nf4vhkzy6st5',
  monthly: 'pri_01htq3vq91c18n974hrf84c17q',
}

export default {
  name: 'MobileCheckout',
  data() {
    return { deviceId: null, customerEmail: '' }
  },
  async mounted() {
    document.documentElement.classList.add('mc-scroll')
    document.body.classList.add('mc-scroll')

    this.deviceId = getPassthroughId()
    const plan = getSelectedPlan() || 'monthly'
    const priceId = PRICE_MAP[plan] || PRICE_MAP.annual

    // Already subscribed? Redirect to callback page instead of opening checkout
    const status = localStorage.getItem('purchaseStatus')
    const exp = localStorage.getItem('expirationTime')
    if (status === 'purchased' && exp && new Date(exp) > new Date()) {
      this.customerEmail = localStorage.getItem('email') || ''
      this.triggerDeepLink('restored')
      return
    }

    // Dev backdoor
    if (this.deviceId === 'emperinter#@outlook.com') {
      this.customerEmail = 'emperinter#@outlook.com'
      this.grantDevAccess(PRICE_MAP.annual)
      return
    }

    // Open Paddle checkout immediately — Paddle handles pricing, locale, plan UI
    this.openCheckout(priceId)
  },
  beforeUnmount() {
    document.documentElement.classList.remove('mc-scroll')
    document.body.classList.remove('mc-scroll')
  },
  methods: {
    openCheckout(priceId) {
      try {
        // eslint-disable-next-line no-undef
        Paddle.Initialize({
          token: 'live_08ccd4dc3ab91634d0586e4b147',
          eventCallback: this.handlePaddleEvent,
        })
        // eslint-disable-next-line no-undef
        Paddle.Checkout.open({
          items: [{ priceId, quantity: 1 }],
          ...(this.deviceId ? { customData: { device_id: this.deviceId } } : {}),
          settings: {
            theme: 'light',
            displayMode: 'overlay',
            variant: 'one-page',
            allowLogout: false,
          },
        })
      } catch (e) {
        // If Paddle.js not loaded, redirect to Hosted Checkout URL as fallback
        let url = `https://buy.paddle.com/checkout?price_id=${priceId}`
        if (this.deviceId) url += `&app_user_id=${encodeURIComponent(this.deviceId)}`
        window.location.href = url
      }
    },

    handlePaddleEvent(data) {
      if (data.name === 'checkout.completed') {
        this.customerEmail = data.data?.customer?.email || ''
        const item = data.data?.items?.[0]
        const txnId = data.data?.transactionId || data.data?.id || ''

        const planId = Object.entries(PRICE_MAP).find(([, v]) => v === item?.priceId)?.[0] || 'annual'
        const days = planId === 'annual' ? 365 : 31
        const exp = new Date(); exp.setDate(exp.getDate() + days)

        localStorage.setItem('purchaseStatus', 'purchased')
        localStorage.setItem('expirationTime', exp.toISOString())
        localStorage.setItem('email', this.customerEmail)

        stashPendingTransaction({ transactionId: txnId, priceId: item?.priceId, planId, email: this.customerEmail })

        if (typeof window.gtag !== 'undefined') {
          const v = item?.price?.unitPrice?.amount ? (item.price.unitPrice.amount / 100).toFixed(2) : 1.0
          window.gtag('event', 'purchase', {
            transaction_id: txnId, value: parseFloat(v),
            currency: item?.price?.unitPrice?.currencyCode || 'USD',
            items: [{ item_id: item?.priceId, item_name: planId, price: parseFloat(v), quantity: 1 }],
          })
        }

        this.triggerDeepLink(txnId)
      }
    },

    async triggerDeepLink(txnId) {
      await new Promise(r => setTimeout(r, 500))
      openDeepLink(buildDeepLink('payment-success', { txn: txnId, email: this.customerEmail }))
    },

    grantDevAccess(priceId) {
      const exp = new Date(); exp.setDate(exp.getDate() + 365)
      localStorage.setItem('purchaseStatus', 'purchased')
      localStorage.setItem('expirationTime', exp.toISOString())
      localStorage.setItem('email', this.customerEmail)
      stashPendingTransaction({ transactionId: 'dev_' + Date.now(), priceId, planId: 'annual', email: this.customerEmail })
      this.triggerDeepLink('dev_' + Date.now())
    },
  },
}
</script>

<style scoped>
.mco {
  min-height: 100dvh;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: clamp(16px, 5vw, 32px);
  background: var(--color-surface-100, #f3f4f6);
  color: var(--color-text-900);
  text-align: center;
}
.mco-hd { margin-bottom: clamp(16px, 4vw, 24px); }
.mco-logo {
  width: clamp(48px, 13vw, 64px); height: clamp(48px, 13vw, 64px);
  border-radius: clamp(14px, 3.5vw, 18px);
  background: linear-gradient(135deg, var(--color-brand-500), var(--color-brand-700));
  display: inline-flex; align-items: center; justify-content: center;
  color: #fff; font-weight: 700; font-size: clamp(22px, 6vw, 30px);
  margin-bottom: clamp(12px, 3vw, 16px);
}
.mco-title { font-size: clamp(16px, 4vw, 20px); font-weight: 700; margin: 0; }
.mco-loading { font-size: clamp(13px, 3.5vw, 15px); color: var(--color-text-400); margin: 0; }
</style>

<style>
html.mc-scroll, body.mc-scroll { overflow: auto !important; height: auto !important; }
</style>